import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject, throwError, timer } from 'rxjs';
import { catchError, tap, retry, finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from '../../notification/notification.service';
import { AuthService } from '../security/auth.service';

@Injectable({ providedIn: 'root' })
export class ApiInterceptor implements HttpInterceptor {
  private readonly silentErrorApis = [
    'api/contact/import-csv',
    'api/phone-numbers/save-phone-numbers'
  ];

  // Maximum number of retry attempts for transient errors
  private readonly maxRetryAttempts = 30;
  // Maximum delay between retries (ms)
  private readonly maxRetryDelayMs = 30000;

  // Track in-flight requests by a stable key so we can cancel the previous one if a new identical request comes in
  private readonly pendingRequests = new Map<string, { cancel$: Subject<void> }>();

  constructor(
    private authService: AuthService,
    private notificationService: NotificationService,
    private cookieService: CookieService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    //console.log('API Interceptor: ' + req.url);
    const canaryCookieBefore = this.cookieService.get('x-beta-customer');

    // Build a stable key for the request
    const key = this.buildRequestKey(req);

    // If there's already an in-flight identical request, cancel it
    const existing = this.pendingRequests.get(key);
    if (existing) {
      try {
        existing.cancel$.next();
        existing.cancel$.complete();
      } catch {}
    }

    // Create a new cancel subject for the current request and store it
    const cancel$ = new Subject<void>();
    this.pendingRequests.set(key, { cancel$: cancel$ });

    return next.handle(req).pipe(
      // Cancel this observable (and underlying XHR) if a new identical request comes in
      takeUntil(cancel$),
      // Retry transient errors with backoff (408, 429, 502, 503)
      retry({
        count: this.maxRetryAttempts,
        delay: (error, retryCount) => {
          if (!(error instanceof HttpErrorResponse)) {
            return throwError(() => error);
          }
          if (!this.isTransientStatus(error.status)) {
            return throwError(() => error);
          }
          const delayMs = this.getRetryDelayMs(error, retryCount);
          return timer(delayMs);
        },
        // Only retry on specified status codes
        resetOnSuccess: true,
      }),
      tap(() => {
        // Success handler
        this.checkCanaryCookie(canaryCookieBefore);
      }),
      catchError(error => {
        // Error handler
        this.handleErrorResponse(error);
        return throwError(() => error);
      }),
      finalize(() => {
        // Clean up only if this is still the latest entry for the key
        const current = this.pendingRequests.get(key);
        if (current && current.cancel$ === cancel$) {
          this.pendingRequests.delete(key);
        }
      })
    );
  }

  /**
   * Checks if the canary cookie has changed and reloads the page if necessary.
   * @param canaryCookieBefore The value of the canary cookie before the request.
   */
  private checkCanaryCookie(canaryCookieBefore: string): void {
    const canaryCookieAfter = this.cookieService.get('x-beta-customer');

    if (canaryCookieBefore !== canaryCookieAfter) {
      console.log('Canary cookie has changed.');
      if (!location.href.includes('/auth/login')) {
        console.log('Reloading page due to canary cookie change.');
        location.reload();
      }
    }
  }

  /**
   * Handles HTTP errors globally.
   * @param error The error response to handle.
   */
  private handleErrorResponse(error: any): void {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 401) {
        // Unauthorized error, redirect to login
        console.log(`HTTP 401 Unauthorized: Logging out ${location.href}`);
        this.authService.logout().catch(err => {
          console.error('Logout error:', err);
        });
      } else {
        this.processServerError(error);
      }
    } else {
      console.error('An unknown error occurred:', error);
      this.notificationService.notifyDanger('An unknown error occurred.');
    }
  }

  /**
   * Processes server errors and displays notifications.
   * @param error The HttpErrorResponse containing the error details.
   */
  private processServerError(error: HttpErrorResponse): void {
    if (this.shouldSuppressError(error?.url)) {
      console.warn('Suppressed error:', error);
      return;
    }

    try {
      let errorBody = error.error;

      if (typeof errorBody === 'string') {
        errorBody = JSON.parse(errorBody);
      }

      if (errorBody && errorBody.status !== 404.109 && errorBody.message) {
        const errorMessage =
          typeof errorBody.message === 'object' ? JSON.stringify(errorBody.message) : errorBody.message;
        this.notificationService.notifyDanger(errorMessage);
      }
    } catch (e) {
      console.error('Error processing server error:', e);
      this.notificationService.notifyDanger('An unknown server error occurred.');
    }
  }

  private shouldSuppressError(errorUrl: string | undefined | null): boolean {
    if (!errorUrl) return false;
    return this.silentErrorApis.some(api => errorUrl.includes(api));
  }

  // Helper: determine if status code is transient and eligible for retry
  private isTransientStatus(status: number): boolean {
    return status === 408 || status === 429 || status === 502 || status === 503;
  }

  // Helper: compute delay with exponential backoff and optional Retry-After support
  private getRetryDelayMs(error: HttpErrorResponse, retryCount: number): number {
    // Respect Retry-After header for 429/503 if present
    const retryAfterHeader = error.headers?.get('Retry-After');
    if (retryAfterHeader && (error.status === 429 || error.status === 503)) {
      const headerMs = this.parseRetryAfterToMs(retryAfterHeader);
      if (headerMs != null) {
        return Math.min(headerMs, this.maxRetryDelayMs);
      }
    }

    // Exponential backoff with jitter: base 1000ms * 2^(attempt-1)
    const base = 1000 * Math.pow(2, Math.max(0, retryCount - 1));
    const jitter = Math.floor(Math.random() * 250); // up to 250ms jitter to avoid thundering herd
    return Math.min(base + jitter, this.maxRetryDelayMs);
  }

  // Helper: parse Retry-After header value (seconds or HTTP-date) to milliseconds
  private parseRetryAfterToMs(value: string): number | null {
    const seconds = Number(value);
    if (!isNaN(seconds)) {
      return Math.max(0, Math.floor(seconds * 1000));
    }
    const dateMs = Date.parse(value);
    if (!isNaN(dateMs)) {
      const delta = dateMs - Date.now();
      return Math.max(0, delta);
    }
    return null;
  }

  // Helper: build a stable key for the request (method + urlWithParams + normalized body)
  private buildRequestKey(req: HttpRequest<any>): string {
    const method = req.method.toUpperCase();
    const url = req.urlWithParams || req.url;
    // Normalize body for comparison; undefined and null treated the same
    const body = this.stableStringify(req.body);
    return `${method}|${url}|${body}`;
  }

  // Stable stringify with sorted keys to reduce false negatives
  private stableStringify(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
      const seen = new WeakSet();
      const replacer = (_key: string, val: any) => {
        if (val && typeof val === 'object') {
          if (seen.has(val)) return '[Circular]';
          seen.add(val);
          if (Array.isArray(val)) return val.map(v => v);
          // Sort object keys for stable output
          return Object.keys(val)
            .sort()
            .reduce((acc: any, k: string) => {
              acc[k] = (val as any)[k];
              return acc;
            }, {} as any);
        }
        return val;
      };
      return JSON.stringify(value, replacer);
    } catch {
      return String(value);
    }
  }
}
